import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Checkbox, checkboxClasses, FormControlLabel, FormGroup } from '@mui/material';
import identity from 'lodash/identity';
import { Controller, useFormContext } from 'react-hook-form';

import { Text } from '@src/components/Typography';
import { palette } from '@src/theme';
import { CheckboxGroupFieldProps } from './interface';

export const CheckboxGroupField = ({
  formatOnChange = identity,
  name,
  options,
  parseValue = identity,
  columns = 1,
}: CheckboxGroupFieldProps) => {
  const columnStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  };

  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();

  const fieldErrors = errors[name];

  const parsedErrors = fieldErrors ? { [name]: parseValue(fieldErrors) } : {};

  const hasErrors = !!parsedErrors[name];

  return (
    <FormGroup style={columns > 1 ? columnStyle : undefined}>
      {options.map((option, i) => (
        <Controller
          key={`${name}${i}`}
          name={name}
          control={control}
          render={({ field: { onChange, value: fieldValue }, fieldState: { error } }) => (
            <FormControlLabel
              key={`${option.label}_${option.value}`}
              control={
                <Checkbox
                  name={name}
                  value={option.value}
                  checked={parseValue(fieldValue)?.includes(option.value)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    let fieldValueCopy = [...(parseValue(fieldValue) || [])];
                    if (checked) {
                      fieldValueCopy.push(option.value);
                    } else {
                      fieldValueCopy = fieldValueCopy.filter((v) => v !== option.value);
                    }
                    const newValue = formatOnChange(fieldValueCopy, fieldValue);
                    onChange(newValue);
                    if (error) {
                      trigger(name);
                    }
                  }}
                  color="primary"
                  sx={{
                    color: palette.text.secondary,
                    [`&, &.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
                      color: hasErrors
                        ? palette.state.error.secondary
                        : palette.state.teal.secondary,
                    },
                  }}
                />
              }
              label={option.label}
            />
          )}
        />
      ))}
      <ErrorMessage
        name={name}
        errors={parsedErrors}
        render={({ message }) => (
          <Text size="s" color="state.error.secondary">
            {message}
          </Text>
        )}
      />
    </FormGroup>
  );
};
